import { useState, useCallback, useEffect } from 'react';
import type { EPIRequest, Incident, Denunciation, StatusAnalise, StatusSolicitacao } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { uploadFormFileToGoogleDrive, type DriveUploadFormType } from '@/lib/googleDrive';
import { notifyFormSubmission } from '@/lib/formNotifications';

export type SubmissionType = 'epi' | 'incident' | 'denunciation';

export interface SubmissionCounts {
  total: number;
  epi: number;
  incident: number;
  denunciation: number;
  epiPendentes: number;
  epiAprovadas: number;
  epiNegadas: number;
}

export interface EPISubmissionData {
  setor: string;
  motivo: string;
  urgencia: string;
  observacao: string;
  itens: { tipo: string; q01: number; q02: number; qCaixa: number; outroDescricao?: string }[];
}

export interface IncidentSubmissionData {
  nome: string;
  setor: string;
  noSetor: string;
  tipoIncidente: string;
  descricao: string;
  arquivo?: File | null;
  fotoUrl?: string;
  declaracaoVerdade: boolean;
}

export interface DenunciationSubmissionData {
  identificar: boolean;
  nomeIdentificado?: string;
  setor: string;
  assedioExplicado: boolean;
  tipoAssedio: string;
  autoresNome: string;
  arquivo?: File | null;
  anexoUrl?: string;
  detalhes: string;
  nivelAssedio: number;
  ultimaOcorrencia: string;
  acessoRetorno: string;
  observacoes: string;
}

type DisplayIdPrefix = 'epi' | 'inc' | 'den';

function formatDisplayId(prefix: DisplayIdPrefix, number: number) {
  return `${prefix}-${String(number).padStart(3, '0')}`;
}

export function useSubmissions() {
  const { user } = useAuth();
  const [epiRequests, setEPIRequests] = useState<EPIRequest[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [denunciations, setDenunciations] = useState<Denunciation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = user?.perfil === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [epiRes, incRes, denRes] = await Promise.all([
        (supabase.from('epi_requests') as any).select('*, epi_request_items(*), profiles!inner(name)').order('created_at', { ascending: false }),
        (supabase.from('incidents') as any).select('*, profiles!inner(name)').order('created_at', { ascending: false }),
        (supabase.from('denunciations') as any).select('*').order('created_at', { ascending: false }),
      ]);

      if (epiRes.error) throw epiRes.error;
      if (incRes.error) throw incRes.error;
      if (denRes.error) throw denRes.error;

      const mappedEPIs: EPIRequest[] = (epiRes.data || []).map((row: Record<string, unknown>) => {
        const profile = row.profiles as Record<string, unknown> | null;
        return {
          id: row.id as string,
          displayId: (row.display_id as string) || undefined,
          userId: row.user_id as string,
          userName: (profile?.name as string) || 'Colaborador',
          setor: row.setor as string,
          motivo: row.motivo as string,
          urgencia: row.urgencia as EPIRequest['urgencia'],
          observacao: (row.observacao as string) || '',
          itens: ((row.epi_request_items as unknown[]) || []).map((item: Record<string, unknown>) => ({
            tipo: item.tipo as string,
            q01: (item.q01 as number) || 0,
            q02: (item.q02 as number) || 0,
            qCaixa: (item.q_caixa as number) || 0,
            outroDescricao: (item.outro_descricao as string) || '',
          })),
          status: row.status as EPIRequest['status'],
          statusReason: (row.status_reason as string) || undefined,
          createdAt: row.created_at as string,
        };
      });

      const mappedIncidents: Incident[] = (incRes.data || []).map((row: Record<string, unknown>) => {
        const profile = row.profiles as Record<string, unknown> | null;
        return {
          id: row.id as string,
          displayId: (row.display_id as string) || undefined,
          userId: row.user_id as string,
          userName: (profile?.name as string) || (row.nome as string) || 'Colaborador',
          setor: row.setor as string,
          noSetor: row.no_setor as string,
          tipoIncidente: row.tipo_incidente as string,
          descricao: row.descricao as string,
          fotoUrl: (row.foto_url as string) || undefined,
          declaracaoVerdade: (row.declaracao_verdade as boolean) || false,
          status: (row.status as StatusAnalise) || 'Em análise',
          statusReason: (row.status_reason as string) || undefined,
          createdAt: row.created_at as string,
        };
      });

      const mappedDenunciations: Denunciation[] = (denRes.data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        displayId: (row.display_id as string) || undefined,
        userId: (row.user_id as string) || undefined,
        identificar: (row.identificar as boolean) || false,
        nomeIdentificado: (row.nome_identificado as string) || undefined,
        setor: row.setor as string,
        assedioExplicado: (row.assedio_explicado as boolean) || false,
        tipoAssedio: row.tipo_assedio as string,
        autoresNome: row.autores_nome as string,
        anexoUrl: (row.anexo_url as string) || undefined,
        detalhes: row.detalhes as string,
        nivelAssedio: row.nivel_assedio as number,
        ultimaOcorrencia: row.ultima_ocorrencia as string,
        acessoRetorno: (row.acesso_retorno as string) || '',
        observacoes: (row.observacoes as string) || '',
        status: (row.status as StatusAnalise) || 'Em análise',
        statusReason: (row.status_reason as string) || undefined,
        createdAt: row.created_at as string,
      }));

      setEPIRequests(mappedEPIs);
      setIncidents(mappedIncidents);
      setDenunciations(mappedDenunciations);
    } catch (err) {
      setError(err instanceof Error ?err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const counts: SubmissionCounts = {
    total: epiRequests.length + incidents.length + denunciations.length,
    epi: epiRequests.length,
    incident: incidents.length,
    denunciation: denunciations.length,
    epiPendentes: epiRequests.filter((r) => r.status === 'Pendente').length,
    epiAprovadas: epiRequests.filter((r) => r.status === 'Aprovado').length,
    epiNegadas: epiRequests.filter((r) => r.status === 'Negado').length,
  };

  const uploadToDrive = useCallback(async ({
    formType,
    submissionId,
    file,
  }: {
    formType: DriveUploadFormType;
    submissionId: string;
    file: File;
  }) => {
    return uploadFormFileToGoogleDrive({ formType, submissionId, file });
  }, []);

  const getNextDisplayId = useCallback(async (table: string, prefix: DisplayIdPrefix) => {
    const { data: nextId, error: rpcError } = await (supabase as any).rpc('next_submission_display_id', {
      target_table: table,
      prefix,
    });

    if (!rpcError && typeof nextId === 'string' && nextId) {
      return nextId;
    }

    const { data, error } = await (supabase.from(table) as any)
      .select('display_id')
      .like('display_id', `${prefix}-%`);

    if (error) throw error;

    const maxNumber = ((data || []) as Array<{ display_id?: string | null }>)
      .map((row) => Number.parseInt((row.display_id || '').replace(`${prefix}-`, ''), 10))
      .filter((value) => Number.isFinite(value))
      .reduce((max, value) => Math.max(max, value), 0);

    return formatDisplayId(prefix, maxNumber + 1);
  }, []);

  const submitEPI = useCallback(async (data: EPISubmissionData) => {
    if (!user) throw new Error('Usuário não autenticado');

    const displayId = await getNextDisplayId('epi_requests', 'epi');

    const { data: request, error: reqError } = await (supabase.from('epi_requests') as any)
      .insert({
        display_id: displayId,
        user_id: user.id,
        setor: data.setor,
        motivo: data.motivo,
        urgencia: data.urgencia,
        observacao: data.observacao,
      })
      .select('id')
      .single();

    if (reqError) throw reqError;

    const items = data.itens.map((item) => ({
      request_id: request.id,
      tipo: item.tipo,
      q01: item.q01,
      q02: item.q02,
      q_caixa: item.qCaixa,
      outro_descricao: item.outroDescricao || '',
    }));

    const { error: itemsError } = await (supabase.from('epi_request_items') as any)
      .insert(items);

    if (itemsError) throw itemsError;

    void notifyFormSubmission({
      formType: 'epi',
      displayId,
      requesterName: user.name,
      setor: data.setor,
      summary: `Motivo: ${data.motivo}. Urgência: ${data.urgencia}. Itens: ${data.itens.map((item) => item.tipo).join(', ')}`,
    });

    await fetchAll();
  }, [user, fetchAll, getNextDisplayId]);

  const submitIncident = useCallback(async (data: IncidentSubmissionData) => {
    if (!user) throw new Error('Usuário não autenticado');

    const incidentId = crypto.randomUUID();
    const displayId = await getNextDisplayId('incidents', 'inc');
    const driveLink = data.arquivo
      ?(await uploadToDrive({
        formType: 'incident',
        submissionId: displayId,
        file: data.arquivo,
      })).webViewLink
      : data.fotoUrl || '';

    const { error } = await (supabase.from('incidents') as any)
      .insert({
        id: incidentId,
        display_id: displayId,
        user_id: user.id,
        nome: data.nome,
        setor: data.setor,
        no_setor: data.noSetor,
        tipo_incidente: data.tipoIncidente,
        descricao: data.descricao,
        foto_url: driveLink,
        declaracao_verdade: data.declaracaoVerdade,
        status: 'Em análise',
      });

    if (error) throw error;

    void notifyFormSubmission({
      formType: 'incident',
      displayId,
      requesterName: data.nome || user.name,
      setor: data.setor,
      summary: `Tipo: ${data.tipoIncidente}. Descrição: ${data.descricao}`,
    });

    await fetchAll();
  }, [user, fetchAll, uploadToDrive, getNextDisplayId]);

  const submitDenunciation = useCallback(async (data: DenunciationSubmissionData) => {
    const denunciationId = crypto.randomUUID();
    const displayId = await getNextDisplayId('denunciations', 'den');
    const driveLink = data.arquivo
      ?(await uploadToDrive({
        formType: 'denunciation',
        submissionId: displayId,
        file: data.arquivo,
      })).webViewLink
      : data.anexoUrl || '';

    const { error } = await (supabase.from('denunciations') as any)
      .insert({
        id: denunciationId,
        display_id: displayId,
        user_id: data.identificar ?user?.id || null : null,
        identificar: data.identificar,
        nome_identificado: data.nomeIdentificado || '',
        setor: data.setor,
        assedio_explicado: data.assedioExplicado,
        tipo_assedio: data.tipoAssedio,
        autores_nome: data.autoresNome,
        anexo_url: driveLink,
        detalhes: data.detalhes,
        nivel_assedio: data.nivelAssedio,
        ultima_ocorrencia: data.ultimaOcorrencia,
        acesso_retorno: data.acessoRetorno,
        observacoes: data.observacoes || '',
        status: 'Em análise',
      });

    if (error) throw error;

    void notifyFormSubmission({
      formType: 'denunciation',
      displayId,
      requesterName: data.identificar ? data.nomeIdentificado || user?.name || 'Identificada' : 'Anônima',
      setor: data.setor,
      summary: `Tipo: ${data.tipoAssedio}. Nível: ${data.nivelAssedio}/9. Detalhes: ${data.detalhes}`,
    });

    await fetchAll();
  }, [user, fetchAll, uploadToDrive, getNextDisplayId]);

  const updateEPIStatus = useCallback(async (id: string, status: StatusSolicitacao, statusReason = '') => {
    if (!isAdmin) throw new Error('Acesso negado');

    let { error } = await (supabase.from('epi_requests') as any)
      .update({ status, status_reason: status === 'Negado' ? statusReason : '' })
      .eq('id', id);

    if (error && /status_reason/i.test(error.message || '')) {
      const fallback = await (supabase.from('epi_requests') as any)
        .update({ status })
        .eq('id', id);
      error = fallback.error;
    }

    if (error) throw error;

    await fetchAll();
  }, [isAdmin, fetchAll]);

  const updateIncidentStatus = useCallback(async (id: string, status: StatusAnalise, statusReason = '') => {
    if (!isAdmin) throw new Error('Acesso negado');

    let { error } = await (supabase.from('incidents') as any)
      .update({ status, status_reason: status === 'Concluído' ? statusReason : '' })
      .eq('id', id);

    if (error && /status_reason/i.test(error.message || '')) {
      const fallback = await (supabase.from('incidents') as any)
        .update({ status })
        .eq('id', id);
      error = fallback.error;
    }

    if (error) throw error;

    await fetchAll();
  }, [isAdmin, fetchAll]);

  const updateDenunciationStatus = useCallback(async (id: string, status: StatusAnalise, statusReason = '') => {
    if (!isAdmin) throw new Error('Acesso negado');

    let { error } = await (supabase.from('denunciations') as any)
      .update({ status, status_reason: status === 'Concluído' ? statusReason : '' })
      .eq('id', id);

    if (error && /status_reason/i.test(error.message || '')) {
      const fallback = await (supabase.from('denunciations') as any)
        .update({ status })
        .eq('id', id);
      error = fallback.error;
    }

    if (error) throw error;

    await fetchAll();
  }, [isAdmin, fetchAll]);

  return {
    epiRequests,
    incidents,
    denunciations,
    counts,
    loading,
    error,
    submitEPI,
    submitIncident,
    submitDenunciation,
    updateEPIStatus,
    updateIncidentStatus,
    updateDenunciationStatus,
    refresh: fetchAll,
  };
}
